/**
 * Prefetch entry point.
 *
 * Executes before the React app mounts to seed React Query caches.
 * Creates a QueryClient, exposes it on window, and executes the appropriate
 * prefetch entry based on the current route.
 *
 * This script is loaded via <script> tag in index.html and runs synchronously.
 */

import * as Sentry from '@sentry/react';

import { mark } from '@/lib/boot/bootMetrics';
import { startBootRequests } from '@/lib/boot/bootRequests';
import { applyPerfQueryParam } from '@/lib/perf/perfFlag';
import { prefetchDebug } from '@/lib/prefetch/debug';
import { hydratePersistedQueries } from '@/lib/prefetch/hydratePersistedQueries';
import { parseOrgRoute } from '@/lib/prefetch/parseOrgRoute';
import { getEntryForPath } from '@/lib/prefetch/registry';
import { isBookingHost } from '@/lib/scheduling/bookingHosts';

import { initAllauth } from './lib/allauthInit';
// Import the shared singleton QueryClient. main.tsx imports the same
// module, so both get the exact same instance regardless of which entry
// parses/executes first. No window stash, no instance mismatch race.
import { queryClient } from './queryClient';

// Persist `?ahoy_perf=1|0` before anything reads the perf flag — this is the
// earliest JS in the page, so the whole boot sequence sees the same setting.
applyPerfQueryParam();
mark('prefetch:script-start');

// Start the session + org-list requests every authenticated boot needs, so
// they overlap with the rest of the bundle evaluating instead of waiting for
// the providers to mount. Booking hosts have no session or orgs to fetch.
initAllauth();
if (!isBookingHost()) {
  startBootRequests();
}

// Parse current route
const route = parseOrgRoute(window.location.pathname);
prefetchDebug('prefetch.ts top-level', {
  pathname: window.location.pathname,
  route,
  queryClientId: (queryClient as unknown as Record<string, unknown>).__debugId,
});

// Execute prefetch if route is org-scoped and has a registered entry
if (route) {
  hydratePersistedQueries(queryClient, route.orgId);

  const entry = getEntryForPath(route.pagePath);
  prefetchDebug('registry lookup', {
    pagePath: route.pagePath,
    found: !!entry,
    entryType: entry?.type,
  });

  if (entry) {
    // Fire-and-forget execution with error suppression.
    // Prefetch failures are non-fatal — the main app will retry if needed.
    if (entry.type === 'combined') {
      entry.execute(route.orgId, queryClient).catch(error => {
        Sentry.captureException(error, {
          tags: { component: 'prefetch', entry_type: 'combined' },
          extra: { orgId: route.orgId, pathname: window.location.pathname },
        });
      });
    } else {
      // Simple entry type
      queryClient
        .prefetchQuery(entry.queryOptions(route.orgId))
        .catch(error => {
          Sentry.captureException(error, {
            tags: { component: 'prefetch', entry_type: 'simple' },
            extra: { orgId: route.orgId, pathname: window.location.pathname },
          });
        });
    }
  }
}
